import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchAuditTrail } from '../../../services/auditTrailApi';
import type { AuditTrailItem, FetchAuditTrailParams } from './auditTrailTypes';
import { logActivitySearch } from '../../../../audit/activityAudit';

export function useAuditTrailEntityHistoryQuery(
  projectId: number | undefined,
  entityType: string,
  entityId: string,
  projectName?: string | null
) {
  const [debouncedEntityId, setDebouncedEntityId] = useState('');
  const [items, setItems] = useState<AuditTrailItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedEntityId(entityId.trim()), 350);
    return () => window.clearTimeout(t);
  }, [entityId]);

  const browseAllTypes = entityType === '__any__';
  const resolvedEntityType = browseAllTypes ? '' : entityType.trim();
  const canBrowse = (Boolean(resolvedEntityType) || browseAllTypes) && !debouncedEntityId;
  const canSearch = Boolean(debouncedEntityId);

  const load = useCallback(async () => {
    if (!canBrowse && !canSearch) {
      setItems([]);
      setTotal(0);
      setError(null);
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);

    const params: FetchAuditTrailParams = {
      limit: 100,
      offset: 0,
      sortBy: 'created_at',
      sortOrder: 'DESC',
      signal: controller.signal,
    };

    if (projectId != null && Number.isFinite(projectId)) {
      params.project_id = projectId;
    }
    if (resolvedEntityType) {
      params.entity_type = resolvedEntityType;
    }
    if (debouncedEntityId) {
      params.search = debouncedEntityId;
      logActivitySearch('entity-history', debouncedEntityId, projectId, projectName);
    }

    const result = await fetchAuditTrail(params);

    if (controller.signal.aborted) return;

    setLoading(false);

    if (!result.ok) {
      if (result.error === 'Request cancelled') return;
      setItems([]);
      setTotal(0);
      setError(result.error);
      return;
    }

    setItems(result.data.items);
    setTotal(result.data.total);
  }, [projectId, projectName, entityType, debouncedEntityId, canBrowse, canSearch, browseAllTypes, resolvedEntityType]);

  useEffect(() => {
    void load();
    return () => abortRef.current?.abort();
  }, [load]);

  return {
    items,
    total,
    loading,
    error,
    reload: load,
    hasQuery: canBrowse || canSearch,
    isBrowseMode: canBrowse,
  };
}
